<template>
    <AuthLayout>
        <div
            class="flex w-full flex-col items-stretch rounded-[8px] bg-white px-[22px] pt-[42px] pb-[42px] sm:max-w-[374px] rounded-[12.8px] border border-[#E5E7EB]"
        >
            <div class="flex justify-center">
                <div
                    class="robot-anim h-[160px] w-[160px]"
                    aria-hidden="true"
                    v-html="robotSvgRaw"
                />
            </div>

            <div
                class="mt-6 text-center text-3xl font-bold leading-9 text-[#161E2E]"
            >
                {{ $t("vue.all_done_logged_out") }}
            </div>

            <div
                class="mt-6 max-w-[344px] text-center text-[12.25px] font-normal leading-[1.42] text-black"
            >
                {{ $t("vue.sorry_to_see_you_go") }}
            </div>

            <div class="mt-6 w-full">
                <inertia-link :href="route('login')" class="block w-full">
                    <MegaButton
                        class="w-full !rounded-md !bg-[#088BEA] !py-3 !text-sm !font-medium !text-white !shadow-none hover:!bg-[#067DD2] focus:!ring-0"
                    >
                        {{ $t("vue.sign_in_again") }}
                    </MegaButton>
                </inertia-link>
            </div>
        </div>
    </AuthLayout>
</template>

<script lang="ts" setup>
import route from "ziggy-js";
import robotSvgRaw from "@/assets/images/dmarceye-robot.svg?raw";
</script>

<style scoped>
.robot-anim {
    overflow: hidden;
    line-height: 0;
    isolation: isolate;
}

.robot-anim :deep(svg) {
    width: 160px;
    height: 160px;
    display: block;
}

.robot-anim :deep(#robot) {
    transform-box: fill-box;
    transform-origin: 50% 60%;
    animation: robotFloat 4.6s ease-in-out infinite;
    will-change: transform;
}

@keyframes robotFloat {
    0%,
    100% {
        transform: translateY(0) rotate(0deg);
    }
    50% {
        transform: translateY(-6px) rotate(-0.8deg);
    }
}

.robot-anim :deep(#robotHead) {
    transform-box: fill-box;
    transform-origin: 45% 65%;
    animation: headBob 4.6s ease-in-out infinite;
    will-change: transform;
}

@keyframes headBob {
    0%,
    100% {
        transform: translateY(0) rotate(0deg);
    }
    50% {
        transform: translateY(-1px) rotate(0.6deg);
    }
}

.robot-anim :deep(#robotEyes) {
    transform-box: fill-box;
    transform-origin: 50% 50%;
    animation: blink 6.8s ease-in-out infinite;
    will-change: transform;
}

@keyframes blink {
    0%,
    7%,
    100% {
        transform: scaleY(1);
    }
    8% {
        transform: scaleY(0.12);
    }
    9% {
        transform: scaleY(1);
    }
    58% {
        transform: scaleY(1);
    }
    59% {
        transform: scaleY(0.12);
    }
    60% {
        transform: scaleY(1);
    }
}

.robot-anim :deep(#robotArm) {
    transform-box: fill-box;
    transform-origin: 20% 30%;
    animation: wave 7.6s ease-in-out infinite;
    will-change: transform;
}

@keyframes wave {
    0%,
    72%,
    100% {
        transform: rotate(0deg);
    }
    78% {
        transform: rotate(-10deg);
    }
    82% {
        transform: rotate(8deg);
    }
    86% {
        transform: rotate(-8deg);
    }
    90% {
        transform: rotate(6deg);
    }
    94% {
        transform: rotate(0deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .robot-anim :deep(#robot),
    .robot-anim :deep(#robotHead),
    .robot-anim :deep(#robotEyes),
    .robot-anim :deep(#robotArm) {
        animation: none !important;
    }
}

.robot-anim :deep(#robotMessage) {
    opacity: 0;
    transform-box: fill-box;
    transform-origin: 50% 100%;
    transform: translateY(-2px) scale(0.92);
    animation: robotMessagePop 5.6s ease-in-out infinite;
    will-change: transform, opacity;
}

@keyframes robotMessagePop {
    0%,
    10% {
        opacity: 0;
        transform: translateY(4px) scale(0.88);
    }
    14%,
    28% {
        opacity: 1;
        transform: translateY(1px) scale(1);
    }
    34%,
    100% {
        opacity: 0;
        transform: translateY(-2px) scale(0.92);
    }
}

.robot-anim :deep(#robotMessage)::after {
    content: "Bye";
    font:
        700 12px/1 system-ui,
        -apple-system,
        Segoe UI,
        Roboto,
        Arial;
    fill: #263238;
    color: #263238;
}

.robot-anim :deep(#robotLeftHand) {
    transform-box: fill-box;
    transform-origin: 78% 28%;
    animation: waveLeft 2.6s ease-in-out infinite;
    will-change: transform;
}

@keyframes waveLeft {
    0%,
    70%,
    100% {
        transform: rotate(0deg);
    }
    74% {
        transform: rotate(-15deg);
    }
    78% {
        transform: rotate(12deg);
    }
    82% {
        transform: rotate(-12deg);
    }
    86% {
        transform: rotate(9deg);
    }
    90% {
        transform: rotate(0deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .robot-anim :deep(#robotLeftHand) {
        animation: none !important;
    }
}
</style>
